import type {
  CreatedIntakeForm,
  CreateIntakeFormRequest,
  GenericFormText,
  IntakeFormListItem,
  IntakeFormStarterTemplate,
  ReplacedIntakeForm,
  ReplaceIntakeFormRequest,
  UpdatedGenericForm,
  UpdateGenericFormTextRequest,
} from '@/definitions/intakeForm'
import api from '@/services/api'

// `base_form_id` is a free-form column rather than a UUID, so a value carrying a
// slash would otherwise build a request to a different path.
const genericFormPath = (orgId: string, baseFormId: string) =>
  `/api/v1/forms/${orgId}/generic/${encodeURIComponent(baseFormId)}`

const customFormPath = (orgId: string, baseFormId: string) =>
  `/api/v1/forms/${orgId}/custom/${encodeURIComponent(baseFormId)}`

// Bounded because the definition box cannot be typed in until this settles, and
// the shared client sets no timeout — a connection that hangs rather than fails
// would leave the operator on a spinner with no way to paste their own definition.
const STARTER_TEMPLATE_TIMEOUT_MS = 10_000

// Bounded for a harder version of the same reason: the edit box shows no fields
// at all until this settles, so a connection that hangs rather than fails leaves
// the operator on a spinner with nothing to do.
const GENERIC_FORM_TEXT_TIMEOUT_MS = 10_000

// Longer than the reads, but bounded: the modal can't be closed mid-write, so a
// hung request leaves only a reload, which discards everything typed.
const WRITE_TIMEOUT_MS = 30_000

/**
 * The definition the new-form box opens pre-filled with.
 *
 * Served rather than bundled here so it travels with the validator that has to
 * accept it. Not organization-specific — every organization starts from this one.
 */
export const FetchIntakeFormStarterTemplate =
  async (): Promise<IntakeFormStarterTemplate> => {
    const response = await api.get<IntakeFormStarterTemplate>(
      '/api/v1/forms/starter-template',
      { timeout: STARTER_TEMPLATE_TIMEOUT_MS }
    )
    return response.data
  }

/**
 * Every intake form the organization has, generic and custom alike.
 *
 * One request however many forms it has, and one entry per form rather than one
 * per revision — the response carries each form's current state.
 */
export const FetchIntakeForms = async (
  orgId: string
): Promise<IntakeFormListItem[]> => {
  const response = await api.get<IntakeFormListItem[]>(`/api/v1/forms/${orgId}`)
  return response.data
}

/**
 * Store a custom intake form. It is live at its patient link once this returns.
 *
 * Refuses a definition carrying warnings until `acknowledge_warnings` is set, so
 * a caller has to show them before anything is stored.
 */
export const CreateIntakeForm = async (
  orgId: string,
  data: CreateIntakeFormRequest
): Promise<CreatedIntakeForm> => {
  const response = await api.post<CreatedIntakeForm>(
    `/api/v1/forms/${orgId}`,
    data,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}

/**
 * The text a generic intake form currently serves, for an edit to start from.
 *
 * Read separately from the forms list rather than carried on each row: the list
 * does not load form definitions, and this reads into one.
 */
export const FetchGenericFormText = async (
  orgId: string,
  baseFormId: string
): Promise<GenericFormText> => {
  const response = await api.get<GenericFormText>(genericFormPath(orgId, baseFormId), {
    timeout: GENERIC_FORM_TEXT_TIMEOUT_MS,
  })
  return response.data
}

/**
 * Change a generic intake form's title, intro text and completion text.
 *
 * The change is stored as a new revision of the same form, so the patient link
 * already given out keeps working and serves the new text. Returns the form's
 * updated state, whose `display_name` is the new title.
 *
 * Sending the current values writes nothing, which is what makes a
 * sequential retry safe. `form_version` is the revision the submitted text was
 * read off: a save built off one since superseded is refused rather than
 * reverting the fields the operator never touched.
 */
export const UpdateGenericFormText = async (
  orgId: string,
  baseFormId: string,
  data: UpdateGenericFormTextRequest
): Promise<UpdatedGenericForm> => {
  const response = await api.put<UpdatedGenericForm>(
    genericFormPath(orgId, baseFormId),
    data,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}

/**
 * Replace a custom intake form's definition with a revised one.
 *
 * Stored as a new revision of the same form, so the patient link already given
 * out keeps working. A form that is switched off stays off. Refuses a definition
 * carrying warnings until `acknowledge_warnings` is set, on the same terms as a
 * first upload. `form_version` is the revision being replaced: one built off a
 * revision since superseded is refused rather than guessing which definition won.
 */
export const ReplaceIntakeForm = async (
  orgId: string,
  baseFormId: string,
  data: ReplaceIntakeFormRequest
): Promise<ReplacedIntakeForm> => {
  const response = await api.put<ReplacedIntakeForm>(
    customFormPath(orgId, baseFormId),
    data,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}

const formEnabledPath = (orgId: string, baseFormId: string, action: 'enable' | 'disable') =>
  `/api/v1/forms/${orgId}/${encodeURIComponent(baseFormId)}/${action}`

/**
 * Put a switched-off intake form back in use. Its patient link works again,
 * including links already sent. A form already in use is left as it is.
 */
export const EnableIntakeForm = async (
  orgId: string,
  baseFormId: string
): Promise<IntakeFormListItem> => {
  const response = await api.post<IntakeFormListItem>(
    formEnabledPath(orgId, baseFormId, 'enable'),
    undefined,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}

/**
 * Take an intake form out of use. Patients who open its link are told the form
 * is not available. A form already switched off is left as it is.
 */
export const DisableIntakeForm = async (
  orgId: string,
  baseFormId: string
): Promise<IntakeFormListItem> => {
  const response = await api.post<IntakeFormListItem>(
    formEnabledPath(orgId, baseFormId, 'disable'),
    undefined,
    { timeout: WRITE_TIMEOUT_MS }
  )
  return response.data
}
